import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import {
    createAvailableStructureSetViewFromDomain,
    createAvailableStructureSetViewFromSession,
} from '~/domain/projections/factories';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { useDomainVersion } from './useDomainVersion';

/**
 * Returns the merged "loaded + session-discoverable" structure-set list
 * as immutable view projections.
 *
 * Loaded items carry their domain {@link StructureSetId} so consumers
 * can pass the id to a domain service. Items known only from the
 * session payload (not yet downloaded) carry just the SOP UID and
 * display name.
 *
 * Tolerates the absence of an active patient session, so it can render in
 * the first commit, before `PatientSessionProvider`'s effect creates the
 * session. While there is no session, only the session-payload items are
 * returned.
 *
 * Replaces the legacy `useAvailableStructureSets`, which exposed a live
 * {@link StructureSet} reference and allowed UI code to navigate into
 * the entity graph.
 *
 * @param showSelected If true, include the currently-active SS in the
 *   list. Default false (used for "switch to a different SS" pickers).
 */
export function useAvailableStructureSetsView(
    showSelected = false
): AvailableStructureSetView[] {
    const session = useSessionOrNull();
    const sessionData = useRecoilValue(sessionDataAtom);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    // Updates the list when structure sets are added or removed.
    const version = useDomainVersion();

    return useMemo(() => {
        const loaded = (session?.patient.structureSets ?? [])
            .filter(
                (ss) =>
                    showSelected ||
                    ss.dicomUid !== currentStructureSet?.dicomUid
            )
            .map(createAvailableStructureSetViewFromDomain);

        const sessionItems: AvailableStructureSetView[] = [];
        if (sessionData?.payload?.structure_sets) {
            const sessionRefs = [
                ...sessionData.payload.structure_sets,
                ...(sessionData.payload.context_structure_set
                    ? [sessionData.payload.context_structure_set]
                    : []),
            ];
            for (const ref of sessionRefs) {
                if (
                    !ref.sop_instance_uid ||
                    loaded.some(
                        (l) => l.sopInstanceUid === ref.sop_instance_uid
                    )
                ) {
                    continue;
                }
                sessionItems.push(
                    createAvailableStructureSetViewFromSession({
                        sopInstanceUid: ref.sop_instance_uid,
                        displayName: ref.display_name ?? '',
                    })
                );
            }
        }

        return [...loaded, ...sessionItems];
        // version is intentionally a dep so the memo recomputes on
        // domain changes even though it's not referenced in the body.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, sessionData, currentStructureSet, showSelected, version]);
}
